import { useEffect, useState } from 'react';
import { amountSchema, type Document, odometerSchema, type ServiceCategory, type ServiceEvent } from 'used-vehicle-contracts';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { Textarea } from '../../components/ui/Textarea';
import { fitsSchema } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { toOptionalNumber } from '../estimates/estimates-display';
import { SERVICE_CATEGORIES } from '../documents/documents-display';

export interface HistoryEventSubmission {
  id: string | null;
  eventDate: string;
  odometerKm: number | null;
  provider: string;
  category: ServiceCategory;
  description: string;
  amount: number | null;
  documentId: string | null;
}

export interface HistoryEventSheetProps {
  isOpen: boolean;
  /** `null` opens the "record a past job" form; an event opens the correction form. */
  event: ServiceEvent | null;
  /** The vehicle's documents, so an existing invoice or report can be attached as evidence. */
  documents: readonly Document[];
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (submission: HistoryEventSubmission) => void;
}

interface FormState {
  eventDate: string;
  odometerKm: string;
  provider: string;
  category: ServiceCategory;
  description: string;
  amount: string;
  documentId: string;
}

const EMPTY: FormState = {
  eventDate: '',
  odometerKm: '',
  provider: '',
  category: 'ROUTINE',
  description: '',
  amount: '',
  documentId: '',
};

const fromEvent = (event: ServiceEvent | null): FormState =>
  event === null
    ? { ...EMPTY }
    : {
        eventDate: event.eventDate,
        odometerKm: event.odometerKm === null ? '' : String(event.odometerKm),
        provider: event.provider,
        category: event.category,
        description: event.description,
        amount: event.amount === null ? '' : String(event.amount),
        documentId: event.documentId ?? '',
      };

/**
 * One historical service event (PP §8: date, odometer, provider, work category, description, attached
 * invoice).
 *
 * The amount field carries its own warning, in both languages, right under the input: **historical
 * amounts are evidence and never enter CTA costs** (PP AC07). It is worth the pixels — this is the one
 * field on the whole dossier where a person could reasonably expect the number to affect the price, and
 * it does not.
 */
export const HistoryEventSheet = ({ isOpen, event, documents, isSaving, error, onClose, onSubmit }: HistoryEventSheetProps) => {
  const { t } = useI18nTranslation();
  const [form, setForm] = useState<FormState>(() => fromEvent(event));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(fromEvent(event));
      setTouched(false);
    }
  }, [isOpen, event]);

  const change = (patch: Partial<FormState>) => setForm((current) => ({ ...current, ...patch }));

  const dateMissing = form.eventDate.trim() === '';
  const providerMissing = form.provider.trim() === '';
  const descriptionMissing = form.description.trim() === '';

  // `step={1}` and `min={0}` are spinner hints and stop nobody typing `120000.5` or a negative amount;
  // the contract's own schemas are what the service will apply, so they are what the sheet asks (§2.5).
  const odometerKm = toOptionalNumber(form.odometerKm);
  const amount = toOptionalNumber(form.amount);
  const odometerInvalid = !fitsSchema(odometerSchema, odometerKm);
  const amountInvalid = !fitsSchema(amountSchema, amount);

  const canSubmit = !dateMissing && !providerMissing && !descriptionMissing && !odometerInvalid && !amountInvalid;

  const submit = () => {
    setTouched(true);

    if (!canSubmit) {
      return;
    }

    onSubmit({
      id: event?.id ?? null,
      eventDate: form.eventDate,
      odometerKm,
      provider: form.provider.trim(),
      category: form.category,
      description: form.description.trim(),
      amount,
      documentId: form.documentId === '' ? null : form.documentId,
    });
  };

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={event === null ? t('history.addTitle') : t('history.editTitle')}
      description={t('history.sheetBody')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={isSaving}
            onClick={submit}
          >
            {t('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <FormField
          id="history-date"
          label={t('history.eventDate')}
          error={touched && dateMissing ? t('history.eventDateRequired') : undefined}
        >
          <Input
            id="history-date"
            type="date"
            value={form.eventDate}
            hasError={touched && dateMissing}
            onChange={(changed) => change({ eventDate: changed.target.value })}
          />
        </FormField>

        <FormField
          id="history-odometer"
          label={t('history.odometerKm')}
          hint={t('history.odometerHint')}
          error={odometerInvalid ? t('history.odometerInvalid') : undefined}
        >
          <Input
            id="history-odometer"
            type="number"
            min={0}
            step={1}
            value={form.odometerKm}
            hasError={odometerInvalid}
            onChange={(changed) => change({ odometerKm: changed.target.value })}
          />
        </FormField>

        <FormField
          id="history-provider"
          label={t('history.provider')}
          error={touched && providerMissing ? t('history.providerRequired') : undefined}
        >
          <Input
            id="history-provider"
            value={form.provider}
            hasError={touched && providerMissing}
            onChange={(changed) => change({ provider: changed.target.value })}
          />
        </FormField>

        <FormField
          id="history-category"
          label={t('history.category')}
          hint={t('history.categoryHint')}
        >
          <Select
            id="history-category"
            value={form.category}
            onChange={(changed) => change({ category: changed.target.value as ServiceCategory })}
            options={SERVICE_CATEGORIES.map((category) => ({ value: category, label: t(`enums.serviceCategory.${category}`) }))}
          />
        </FormField>

        <FormField
          id="history-description"
          label={t('history.description')}
          error={touched && descriptionMissing ? t('history.descriptionRequired') : undefined}
        >
          <Textarea
            id="history-description"
            rows={3}
            value={form.description}
            hasError={touched && descriptionMissing}
            onChange={(changed) => change({ description: changed.target.value })}
          />
        </FormField>

        <FormField
          id="history-amount"
          label={t('history.amount')}
          hint={t('history.evidenceOnly')}
          error={amountInvalid ? t('estimates.numberOutOfRange') : undefined}
        >
          <Input
            id="history-amount"
            type="number"
            min={0}
            step={0.001}
            value={form.amount}
            hasError={amountInvalid}
            onChange={(changed) => change({ amount: changed.target.value })}
          />
        </FormField>

        <FormField
          id="history-document"
          label={t('history.attachedDocument')}
          hint={t('history.attachedDocumentHint')}
        >
          <Select
            id="history-document"
            value={form.documentId}
            placeholder={t('history.noDocument')}
            onChange={(changed) => change({ documentId: changed.target.value })}
            options={documents.map((document) => ({ value: document.id, label: document.title }))}
          />
        </FormField>
      </div>
    </Sheet>
  );
};
